/**
 * Chapter028.js — OHSE Management Plan Checklist (OHSE 028)
 * Migrated from SWMS Platform Chapter028.tsx → JavaScript
 *
 * Props:
 *   data        {object}  ohse_checklist from swmsData
 *   onChange    {fn}      (newChecklist) => void
 *   companyName {string}  from companyInfo.name
 *   projectName {string}  from derived project info
 */

import { useCallback } from 'react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const textareaCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all resize-y min-h-[52px]';

const OHSE_CHECKLIST_ITEMS = [
  'Changes and distribution of the OHSE Mgt Plan are recorded',
  'Project details / Description of works / Organisation details are current',
  'OHSE Policy signed and dated by Director/Manager',
  'Hazards are IDENTIFIED, and risks are assessed',
  'Controls for high risk activities are documented (Safe Work Method Statement(s))',
  'Training and Competency Register is current',
  'Site Specific Induction Training records are current',
  'SWMS Training is current',
  'Roles and responsibilities are allocated and signed',
  'Consultation arrangements (nature, topics, intervals) are documented',
  'Plant / Equipment Register is current',
  'Hazardous Substances / Dangerous Goods Register is current',
  'Personal Protective Equipment Register is current',
  'Periodic Workplace Inspection Checklists are completed',
  'Register of Injuries is current',
  'Incident Investigation Reports are completed',
  'Hazard Reports are completed',
  'Electrical Equipment Register is current',
  'Injury Management and Return-to-Work Program is displayed',
  'Workers Compensation Information is current',
  'Other',
];

function ConformsTickboxes({ value, onChange }) {
  return (
    <div className="flex items-center gap-4">
      <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
        <input type="checkbox" checked={value === 'Yes'}
          onChange={() => onChange('Yes')}
          className="w-4 h-4 rounded border-[#c9a84c] text-[#c9a84c] focus:ring-[#c9a84c] focus:ring-offset-0" />
        <span className="text-sm text-[#1a1a2e]">Yes</span>
      </label>
      <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
        <input type="checkbox" checked={value === 'No'}
          onChange={() => onChange('No')}
          className="w-4 h-4 rounded border-[#c9a84c] text-[#c9a84c] focus:ring-[#c9a84c] focus:ring-offset-0" />
        <span className="text-sm text-[#1a1a2e]">No</span>
      </label>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function Chapter028({ data = {}, onChange, companyName, projectName = '' }) {
  const cn = companyName || '[Company Name]';

  const updateField = useCallback((field, value) => {
    onChange({ ...data, [field]: value });
  }, [data, onChange]);

  const updateItem = useCallback((itemId, field, value) => {
    onChange({
      ...data,
      items: (data.items || []).map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      ),
    });
  }, [data, onChange]);

  const items = data.items && data.items.length === OHSE_CHECKLIST_ITEMS.length
    ? data.items
    : OHSE_CHECKLIST_ITEMS.map(desc => ({ id: crypto.randomUUID(), description: desc, conforms: '' }));

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 028</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">OHSE Management Plan Checklist</h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
        reviews all OHSE policies and procedures every 3 months to determine the effectiveness of
        the OHSE Management Plan in addressing OHSE in the workplace.
      </div>

      {/* General */}
      <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">General</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Project Name">
          <input type="text" value={projectName} readOnly
            className={inputCls + ' bg-gray-50 cursor-default'} placeholder="[Project Name]" />
        </Field>
        <Field label="Location">
          <input type="text" value={data.location || ''} placeholder="Project location"
            onChange={e => updateField('location', e.target.value)} className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Auditor">
          <input type="text" value={data.auditor || ''} placeholder="Auditor name"
            onChange={e => updateField('auditor', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Other Attendees">
          <textarea value={data.other_attendees || ''} placeholder="List other attendees..."
            onChange={e => updateField('other_attendees', e.target.value)} className={textareaCls} rows={2} />
        </Field>
      </div>

      {/* Checklist Items */}
      <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">Items Identified for Correction</p>
      <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
        <div className="hidden md:grid md:grid-cols-[1fr_140px] gap-2 px-3 py-2 bg-[#f0ede6] border-b border-[#e0dbd0]">
          <span className="text-xs font-semibold text-[#666] uppercase tracking-wider">Activities Reviewed</span>
          <span className="text-xs font-semibold text-[#666] uppercase tracking-wider text-center">Conforms</span>
        </div>
        {items.map((item, index) => (
          <div key={item.id}
            className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-2 items-center px-3 py-2.5 border-b border-[#e8e4dc] last:border-b-0">
            <div className="flex items-start gap-2">
              <span className="text-xs text-[#999] mt-0.5 min-w-[20px] text-right">{index + 1}.</span>
              {item.description === 'Other' ? (
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-sm text-[#1a1a2e] font-medium">Other:</span>
                  <input type="text" placeholder="Specify other item..."
                    value={item.description === 'Other' ? '' : item.description}
                    onChange={e => {
                      const val = e.target.value;
                      updateItem(item.id, 'description', val ? `Other: ${val}` : 'Other');
                    }}
                    className={inputCls + ' flex-1'} />
                </div>
              ) : (
                <span className="text-sm text-[#1a1a2e] leading-snug">{item.description}</span>
              )}
            </div>
            <div className="flex justify-start md:justify-center">
              <ConformsTickboxes value={item.conforms} onChange={v => updateItem(item.id, 'conforms', v)} />
            </div>
          </div>
        ))}
      </div>

      {/* Outstanding Issues */}
      <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">Outstanding Issues and Recommendations</p>
      <textarea value={data.outstanding_issues || ''} placeholder="Document outstanding issues and recommendations..."
        onChange={e => updateField('outstanding_issues', e.target.value)} className={textareaCls} rows={3} />

      {/* Follow Up Actions */}
      <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">Follow Up Actions Required</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Follow up required">
          <div className="flex items-center gap-6 mt-1">
            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input type="checkbox" checked={data.follow_up_required === 'Yes'}
                onChange={() => updateField('follow_up_required', 'Yes')}
                className="w-4 h-4 rounded border-[#c9a84c] text-[#c9a84c] focus:ring-[#c9a84c] focus:ring-offset-0" />
              <span className="text-sm text-[#1a1a2e]">Yes</span>
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input type="checkbox" checked={data.follow_up_required === 'No'}
                onChange={() => updateField('follow_up_required', 'No')}
                className="w-4 h-4 rounded border-[#c9a84c] text-[#c9a84c] focus:ring-[#c9a84c] focus:ring-offset-0" />
              <span className="text-sm text-[#1a1a2e]">No</span>
            </label>
          </div>
        </Field>
        <Field label="When">
          <input type="date" value={data.follow_up_when || ''}
            onChange={e => updateField('follow_up_when', e.target.value)} className={inputCls} />
        </Field>
      </div>

      {/* Completed By */}
      <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">Completed By</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Name">
          <input type="text" value={data.completed_by_name || ''} placeholder="Name"
            onChange={e => updateField('completed_by_name', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Position">
          <input type="text" value={data.completed_by_position || ''} placeholder="Position"
            onChange={e => updateField('completed_by_position', e.target.value)} className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Signature (type name)">
          <input type="text" value={data.completed_by_signature || ''} placeholder="Signature"
            onChange={e => updateField('completed_by_signature', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Date">
          <input type="date" value={data.completed_by_date || ''}
            onChange={e => updateField('completed_by_date', e.target.value)} className={inputCls} />
        </Field>
      </div>
    </div>
  );
}
